/**
 * ============================================================
 * KEJJA OS — TourTip
 * A lightweight, dismissable explainer banner shown on first
 * visit to each key page. Users can replay all tours from
 * Settings → Guide.
 * ============================================================
 */

import { useEffect, useState } from 'react';
import { Sparkles, X, ChevronRight } from 'lucide-react';

const LS_KEY = 'kejja_tours_seen';

function getSeen(): Set<string> {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function markSeen(id: string) {
  try {
    const seen = getSeen();
    seen.add(id);
    localStorage.setItem(LS_KEY, JSON.stringify(Array.from(seen)));
  } catch { /* ignore */ }
}

export function resetAllTours() {
  try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
}

interface TourTipProps {
  id: string;              // unique id like 'dashboard-v1'
  title: string;           // short headline
  body: string;            // 1-2 sentence explainer
  bullets?: string[];      // optional list items
  accent?: 'blue' | 'amber' | 'emerald' | 'rose' | 'purple';
}

export function TourTip({ id, title, body, bullets, accent = 'blue' }: TourTipProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Show only if unseen — compute at mount to avoid hydration flicker
    if (!getSeen().has(id)) setVisible(true);
  }, [id]);

  if (!visible) return null;

  const dismiss = () => {
    markSeen(id);
    setVisible(false);
  };

  const accentClass = {
    blue:    'from-sky-500/10 to-sky-500/5 border-sky-300/40 dark:border-sky-500/30 text-sky-700 dark:text-sky-300',
    amber:   'from-amber-500/10 to-amber-500/5 border-amber-300/40 dark:border-amber-500/30 text-amber-700 dark:text-amber-300',
    emerald: 'from-emerald-500/10 to-emerald-500/5 border-emerald-300/40 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300',
    rose:    'from-rose-500/10 to-rose-500/5 border-rose-300/40 dark:border-rose-500/30 text-rose-700 dark:text-rose-300',
    purple:  'from-purple-500/10 to-purple-500/5 border-purple-300/40 dark:border-purple-500/30 text-purple-700 dark:text-purple-300',
  }[accent];

  return (
    <div className={`relative bg-gradient-to-br ${accentClass} border rounded-xl p-4 mb-5 shadow-sm`}>
      <button
        onClick={dismiss}
        className="absolute top-2 right-2 h-7 w-7 rounded-md hover:bg-black/5 dark:hover:bg-white/10 flex items-center justify-center transition"
        aria-label="Dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>

      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-lg bg-white/70 dark:bg-slate-900/70 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0 pr-6">
          <h3 className="text-sm font-bold">{title}</h3>
          <p className="text-xs mt-1 opacity-85">{body}</p>
          {bullets && bullets.length > 0 && (
            <ul className="mt-2.5 space-y-1">
              {bullets.map((b, i) => (
                <li key={i} className="text-xs flex items-start gap-1.5">
                  <ChevronRight className="w-3 h-3 mt-0.5 flex-shrink-0 opacity-60" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}
          <button
            onClick={dismiss}
            className="mt-3 text-[11px] font-semibold underline underline-offset-2 opacity-80 hover:opacity-100"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
